import CloseIcon from "@assets/icons/new/close_24px.svg?react";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { ReactSetStateFunction } from "@custom-types/types";
import { neutral } from "@flat-ui";
import { IElementAttachment } from "@faro-lotv/ielement-types";
import {
  Box,
  CircularProgress,
  // eslint-disable-next-line no-restricted-imports
  Dialog,
  Typography,
} from "@mui/material";
import { AttachmentItemActions } from "@pages/project-details/project-markups/sidepanel/attachment/attachment-item-actions";
import { useRefreshAttachment } from "@pages/project-details/project-markups/sidepanel/use-refresh-attachment";
import { EDecimalToHex } from "@styles/common-colors";
import { CommonStyles, withEllipsis } from "@styles/common-styles";
import { addTransparency } from "@utils/ui-utils";
import { useEffect, useState } from "react";

interface Props {
  /** The attachment item to display */
  attachment: IElementAttachment;

  /** Check if is downloading the image */
  isDownloading: boolean;

  /** Function to toggle the preview dialog visibility */
  setIsPreviewDialogOpen: (value: boolean) => void;

  /** Function to update the downloading state of the attachment */
  setIsDownloading: (value: boolean) => void;

  /** Function to set the selected attachments */
  setSelectedAttachments: ReactSetStateFunction<Set<string>>;
}

/** Size in pixels for each attachment action */
const ACTION_BTN_SIZE = "1.5rem";

/** Size in pixels for each attachment action icon */
const ACTION_ICON_SIZE = "1.5rem";

/**
 * This component renders a full-screen dialog to preview an attachment. It displays the attachment's image,
 * name, and provides actions for downloading or deleting the attachment.
 */
export function FaroImagePreview({
  attachment,
  isDownloading,
  setIsPreviewDialogOpen,
  setIsDownloading,
  setSelectedAttachments,
}: Props): JSX.Element {
  const { refreshAttachmentIfExpired, isAttachmentExpired } =
    useRefreshAttachment();

  const [currentAttachment, setCurrentAttachment] =
    useState<IElementAttachment>(attachment);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false);

  useEffect(() => {
    async function checkAndUpdateAttachment(): Promise<void> {
      try {
        if (isAttachmentExpired(attachment)) {
          setIsImageLoading(true);

          const updatedAttachment = await refreshAttachmentIfExpired([
            attachment.id,
          ]);
          setCurrentAttachment(updatedAttachment[0]);
          setIsImageLoading(false);
        }
      } catch {
        setIsImageLoading(false);
      }
    }

    void checkAndUpdateAttachment();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- The empty array ensures it runs only once, on mount
  }, []);

  return (
    /**
     * Using the Material UI Dialog here because the Flat UI version doesn't fit
     * the custom layout required for this specific case.
     */
    <Dialog
      open={true}
      fullScreen
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "transparent",
          },
        },
      }}
    >
      <Box
        data-testid="preview-attachment-dialog-content"
        sx={{
          height: "calc(100vh - 2rem)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "70px",
            alignItems: "center",
            boxSizing: "border-box",
            borderBottom: CommonStyles.Borders.gray200Divider,
            backgroundColor: neutral[0],
            zIndex: 1,
            position: "relative",
            justifyContent: "space-between",
            width: "100%",
            px: "1rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <SphereIconButton
              dataTestId="preview-dialog-close-icon"
              component={CloseIcon}
              buttonSize={ACTION_BTN_SIZE}
              iconSize={ACTION_ICON_SIZE}
              iconButtonProps={{
                sx: {
                  padding: "20px",
                },
              }}
              onClick={() => setIsPreviewDialogOpen(false)}
            />
            <Typography
              sx={{
                marginLeft: "10px",
                fontWeight: "700",
                ...withEllipsis,
              }}
              title={currentAttachment.name}
            >
              {currentAttachment.name}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            {isDownloading ? (
              <CircularProgress size={"1rem"} />
            ) : (
              <AttachmentItemActions
                attachment={currentAttachment}
                setIsDownloading={setIsDownloading}
                eventSource="preview attachment"
                buttonSize={ACTION_BTN_SIZE}
                iconSize={ACTION_ICON_SIZE}
                iconBtnProps={{
                  sx: {
                    padding: "20px",
                  },
                }}
                setSelectedAttachments={setSelectedAttachments}
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            padding: { xs: "16px", md: "24px" },
            backgroundColor: addTransparency({
              color: neutral[800],
              alpha: EDecimalToHex.hundredFortyFour,
            }),
            backdropFilter: "blur(15px)",
            overflow: "auto",
          }}
        >
          {isImageLoading ? (
            <CircularProgress
              size={"2rem"}
              sx={{
                color: neutral[0],
              }}
            />
          ) : (
            <Box
              component="img"
              src={currentAttachment.signedUri}
              alt="attachment annotation"
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                width: "auto",
                height: "auto",
              }}
            />
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
